import { Button } from "@/components/ui/button";
import { FullScreenLoader } from "@/components/common/FullScreenLoader";
import { useActionBarHeight } from "@/hooks/use-action-bar-height";
import {
  countTopupTickets,
  EMPTY_TOPUP_COUNTS,
  totalTopupAmount,
  totalTopupTickets,
  validateTopupRoutePricing,
  type TopupRouteEntry,
} from "@/lib/topup";
import { formatCurrency } from "@/utils/formatCurrency";
import { X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { JettySelect } from "@/features/purchase-ticket/JettySelect";
import { PaxSelect } from "@/features/topup/PaxSelect";
import { SectionTitle } from "@/components/common/SectionTitle";
import topUpIcon from "@/assets/icons/topup.svg";
import { TopupRoute, TopupRouteTicket } from "@/services/topup.service";
import { useAuthStore } from "@/stores/auth.store";
import type { TopupRouteData } from "@/types/topup/topuproute";
import type { RouteTicketData } from "@/types/topup/routeticket";
import { toast } from "sonner";

const makeEntry = (
  index: number,
  routeLabel = "",
  operatorCompanyRouteId = "",
): TopupRouteEntry => ({
  id: `entry-${index}-${Math.random().toString(36).slice(2, 8)}`,
  routeLabel,
  operatorCompanyRouteId,
  counts: { ...EMPTY_TOPUP_COUNTS },
});

export default function TopupPage() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<TopupRouteEntry[]>(() => [
    makeEntry(0),
  ]);
  const [loading, setLoading] = useState(true);
  const [routeList, setRouteList] = useState<TopupRouteData[]>([]);
  const [ticketTypesByRoute, setTicketTypesByRoute] = useState<
    Record<string, RouteTicketData[]>
  >({});
  const ticketTypesCache = useRef<Record<string, RouteTicketData[]>>({});

  const fetchTicketTypes = useCallback(async (operatorCompanyRouteId: string) => {
    if (!operatorCompanyRouteId || ticketTypesCache.current[operatorCompanyRouteId]) {
      return;
    }

    try {
      const data = await TopupRouteTicket({
        agentCompanySellerId:
          useAuthStore.getState().user?.agentCompanySellerId ?? "",
        operatorCompanyRouteId,
      });

      ticketTypesCache.current[operatorCompanyRouteId] = data.data;
      setTicketTypesByRoute((current) => ({
        ...current,
        [operatorCompanyRouteId]: data.data,
      }));
    } catch (error) {
      console.error("Failed to fetch top-up route tickets", error);
      toast.error("Failed to load top-up ticket types.");
    }
  }, []);

  useEffect(() => {
    async function fetchRoutes() {
      try {
        setLoading(true);
        const data = await TopupRoute({
          agentCompanySellerId:
            useAuthStore.getState().user?.agentCompanySellerId ?? "",
        });

        const routes = data.data;
        setRouteList(routes);

        const firstRoute = routes[0];
        if (firstRoute) {
          setEntries((current) =>
            current.map((entry) =>
              entry.routeLabel
                ? entry
                : {
                    ...entry,
                    routeLabel: firstRoute.routeName,
                    operatorCompanyRouteId: firstRoute.operatorCompanyRouteId,
                  },
            ),
          );
          fetchTicketTypes(firstRoute.operatorCompanyRouteId);
        }
      } catch (error) {
        console.error("Failed to fetch top-up routes", error);
        toast.error("Failed to load top-up routes.");
      } finally {
        setLoading(false);
      }
    }

    fetchRoutes();
  }, [fetchTicketTypes]);

  const update = (id: string, patch: Partial<TopupRouteEntry>) =>
    setEntries((current) =>
      current.map((entry) =>
        entry.id === id ? { ...entry, ...patch } : entry,
      ),
    );

  const selectRoute = (id: string, routeLabel: string) => {
    const route = routeList.find((item) => item.routeName === routeLabel);

    update(id, {
      routeLabel,
      operatorCompanyRouteId: route?.operatorCompanyRouteId ?? "",
      counts: {},
    });

    if (route) {
      fetchTicketTypes(route.operatorCompanyRouteId);
    }
  };

  const totalTickets = totalTopupTickets(entries);
  const pricingIssues = validateTopupRoutePricing(entries, ticketTypesByRoute);
  const canContinue =
    entries.every((entry) => countTopupTickets(entry.counts) > 0) &&
    pricingIssues.length === 0;

  // Expose the fixed action bar height so the layout's bottom-hero sits above it
  const footerRef = useActionBarHeight<HTMLElement>();

  const handleNext = () => {
    if (!canContinue) {
      const issue = pricingIssues[0] ?? "Please complete the selected top-up route first.";
      toast.error(issue);
      return;
    }

    navigate("/top-up/top-up-details", { state: { entries } });
  };

  return (
    <div className="mx-auto max-w-7xl px-4 pt-4 pb-16 sm:px-6 lg:px-8">
      {loading && <FullScreenLoader />}

      <div className="flex items-center justify-between">
        <SectionTitle img={topUpIcon} title="Top-up" />
      </div>

      <div className="mt-4 flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-[2fr_1fr]">
          <div className="space-y-8">
            {entries.map((entry, index) => (
              <section
                key={entry.id}
                className="rounded-2xl bg-card px-4 py-6 md:p-6 shadow-xl"
              >
                {index > 0 ? (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      aria-label="Remove route"
                      className="text-foreground/70"
                      onClick={() =>
                        setEntries((current) =>
                          current.filter((item) => item.id !== entry.id),
                        )
                      }
                    >
                      <X className="size-6" />
                    </button>
                  </div>
                ) : null}

                <div className="md:flex md:gap-4">
                  <div className="flex-1">
                    <JettySelect
                      label="Route"
                      value={entry.routeLabel}
                      options={routeList.map((route) => route.routeName)}
                      onChange={(routeLabel) =>
                        selectRoute(entry.id, routeLabel)
                      }
                    />
                  </div>

                  <div className="mt-4 md:mt-0 md:flex-1">
                    <PaxSelect
                      label="Top-up Tickets"
                      value={entry.counts}
                      ticketTypes={
                        ticketTypesByRoute[entry.operatorCompanyRouteId ?? ""] ??
                        []
                      }
                      onChange={(counts) => update(entry.id, { counts })}
                    />
                  </div>
                </div>
              </section>
            ))}

            <div className="hidden justify-end gap-8 md:flex">
              <Button
                type="button"
                variant="default"
                className="w-full flex-1 bg-muted-foreground hover:bg-[#2A2A2A] cursor-pointer"
                onClick={() =>
                  setEntries([
                    makeEntry(
                      0,
                      routeList[0]?.routeName,
                      routeList[0]?.operatorCompanyRouteId,
                    ),
                  ])
                }
              >
                Reset
              </Button>
              <Button
                type="button"
                variant="default"
                className="w-full flex-1 cursor-pointer"
                onClick={() =>
                  setEntries((current) => [
                    ...current,
                    makeEntry(current.length),
                  ])
                }
              >
                Add more route
              </Button>
            </div>
          </div>

          <div className="hidden md:block">
            <div className="rounded-2xl bg-card px-4 py-6 shadow-xl">
              {[
                { label: "Total Routes", value: String(entries.length) },
                { label: "Total Tickets", value: String(totalTickets) },
                {
                  label: "Total Amount",
                  value: formatCurrency(
                    totalTopupAmount(entries, ticketTypesByRoute),
                  ),
                },
              ].map((item) => (
                <div
                  key={item.label}
                  className="flex items-center justify-between pb-4"
                >
                  <p className="text-sm font-medium text-muted-foreground">
                    {item.label}
                  </p>
                  <p className="text-sm font-medium text-muted-foreground">
                    {item.value}
                  </p>
                </div>
              ))}
              <Button
                type="button"
                variant="secondary"
                className="w-full cursor-pointer mt-4"
                disabled={!canContinue}
                onClick={handleNext}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 md:hidden">
        <Button
          type="button"
          variant="default"
          className="w-full"
          onClick={() =>
            setEntries((current) => [...current, makeEntry(current.length)])
          }
        >
          Add more route
        </Button>
        <Button
          type="button"
          variant="default"
          className="w-full bg-muted-foreground hover:bg-[#2A2A2A]"
          onClick={() =>
            setEntries([
              makeEntry(
                0,
                routeList[0]?.routeName,
                routeList[0]?.operatorCompanyRouteId,
              ),
            ])
          }
        >
          Reset
        </Button>
      </div>

      <footer
        ref={footerRef}
        className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-card md:hidden"
      >
        <div className="mx-auto w-full max-w-7xl p-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-2.5">
            {[
              { label: "Total Routes", value: String(entries.length) },
              { label: "Total Tickets", value: String(totalTickets) },
              {
                label: "Total Amount",
                value: formatCurrency(
                  totalTopupAmount(entries, ticketTypesByRoute),
                ),
              },
            ].map((item) => (
              <div key={item.label}>
                <p className="text-xs font-medium text-muted-foreground">
                  {item.label}
                </p>
                <p className="text-sm font-medium mt-2 text-field">
                  {item.value}
                </p>
              </div>
            ))}
          </div>
          <Button
            type="button"
            variant="secondary"
            className="w-full cursor-pointer mt-4"
            disabled={!canContinue}
            onClick={handleNext}
          >
            Next
          </Button>
        </div>
      </footer>
    </div>
  );
}
